import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Shield, AlertTriangle, CreditCard, Mail, Clock, RefreshCw } from 'lucide-react';

const ImpressumPage = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: 'Legal Notice (Impressum)',
      company: 'Company Information',
      companyName: 'DC LLC',
      address: 'Montana, USA',
      
      serviceTitle: 'About Our Service',
      serviceText: `VisaAlerts is an independent notification service that monitors TLS Contact, VFS Global, and BLS International 
        appointment availability and sends alerts to subscribers when new slots become available. We are NOT affiliated with, 
        endorsed by, or connected to TLS Contact, VFS Global, or BLS International in any way.`,
      
      disclaimerTitle: 'Important Disclaimer',
      disclaimerText: `VisaAlerts is an independent notification service. We are NOT affiliated with TLS Contact, VFS Global, or BLS International and do NOT book appointments on your behalf. We simply monitor for available slots and notify you so YOU can book directly on the official websites (visas-fr.tlscontact.com, vfsglobal.com, blsinternational.com).`,
      disclaimerPoints: [
        'We do NOT book visa appointments on your behalf',
        'We do NOT have access to TLS Contact, VFS Global, or BLS systems',
        'We only send notification alerts when we detect available slots',
        'You must book your appointment directly on the official websites',
        'We are not responsible for appointment availability or booking success'
      ],
      
      securityTitle: 'Security & Trust',
      securityText: `Your security is our top priority. We want to be completely transparent about our practices:`,
      securityPoints: [
        'We NEVER send phishing SMS or emails',
        'We NEVER include clickable links in our SMS messages',
        'We NEVER ask for payment via SMS or email',
        'We NEVER request your personal data via messages',
        'All our notifications contain plain text information only'
      ],
      
      subscriptionTitle: 'Subscription Terms',
      subscriptionPoints: [
        'Monthly subscription: €7.99/month',
        'Cancel anytime - no long-term commitment',
        'Subscription remains active until end of billing period after cancellation',
        'Payments processed securely via Stripe',
        'No hidden fees or additional charges'
      ],
      
      bookingDisclaimerTitle: 'Important: About Appointment Booking',
      bookingDisclaimerText: `Please note that subscribing to VISAGO-ALERTS does not guarantee a successful visa appointment booking. Our service detects available slots in real time and notifies you immediately via SMS and email. However, visa appointment slots are extremely limited and highly sought after — hundreds of applicants may receive the same alert at the same time.`,
      bookingDisclaimerAdvice: 'To maximize your chances of securing an appointment:',
      bookingDisclaimerPoints: [
        'Stay alert and check your SMS and emails as soon as they arrive',
        'Keep your phone nearby with notifications enabled at all times',
        'Have your visa centre login credentials ready so you can book immediately',
        'Act within seconds — the fastest applicants secure the available slots',
        'Do not rely solely on email; SMS alerts arrive faster and should be your primary notification channel'
      ],
      bookingDisclaimerNote: 'We provide the intelligence — your speed makes the difference.',
      
      refundTitle: '30-Day Satisfaction Guarantee',
      refundText: `We stand behind the quality of our monitoring service. If you subscribe and do not receive a single appointment alert within 30 days of your subscription start date, you are entitled to a full refund — no questions asked.`,
      refundHow: 'How to claim your refund:',
      refundPoints: [
        'Contact us within 30 days of your subscription at customer_service@visago-alerts.online',
        'Include your registered email address and subscription date',
        'If no alerts were sent to your account during this period, you will receive a 100% refund',
        'Refunds are processed within 5-7 business days via the original payment method'
      ],
      refundNote: 'This guarantee applies only if zero alerts were sent for your selected destination and residence country during the 30-day period. If alerts were sent but you were unable to book an appointment, the guarantee does not apply, as appointment availability depends on external visa centres beyond our control.',
      
      contactTitle: 'Contact',
      contactText: 'For any questions or concerns, please use our contact form or email us at:',
      contactEmail: 'customer_service@visago-alerts.online'
    },
    fr: {
      title: 'Mentions Légales (Impressum)',
      company: 'Informations sur l\'entreprise',
      companyName: 'DC LLC',
      address: 'Montana, États-Unis',
      
      serviceTitle: 'À propos de notre service',
      serviceText: `VisaAlerts est un service de notification indépendant qui surveille la disponibilité des rendez-vous 
        TLS Contact, VFS Global et BLS International et envoie des alertes aux abonnés lorsque de nouveaux créneaux deviennent disponibles. 
        Nous ne sommes PAS affiliés à TLS Contact, VFS Global ou BLS International de quelque manière que ce soit.`,
      
      disclaimerTitle: 'Avertissement Important',
      disclaimerText: `VisaAlerts est un service de notification indépendant. Nous ne sommes PAS affiliés à TLS Contact, VFS Global ou BLS International et nous ne réservons PAS de rendez-vous pour vous. Nous surveillons simplement les créneaux disponibles et vous notifions afin que VOUS puissiez réserver directement sur les sites officiels (visas-fr.tlscontact.com, vfsglobal.com, blsinternational.com).`,
      disclaimerPoints: [
        'Nous ne réservons PAS de rendez-vous visa pour vous',
        'Nous n\'avons PAS accès aux systèmes TLS Contact, VFS Global ou BLS',
        'Nous envoyons uniquement des alertes de notification',
        'Vous devez réserver directement sur les sites officiels',
        'Nous ne sommes pas responsables de la disponibilité des rendez-vous'
      ],
      
      securityTitle: 'Sécurité et Confiance',
      securityText: `Votre sécurité est notre priorité absolue. Nous voulons être totalement transparents:`,
      securityPoints: [
        'Nous n\'envoyons JAMAIS de SMS ou d\'emails de phishing',
        'Nous n\'incluons JAMAIS de liens cliquables dans nos SMS',
        'Nous ne demandons JAMAIS de paiement par SMS ou email',
        'Nous ne demandons JAMAIS vos données personnelles par message',
        'Toutes nos notifications contiennent uniquement du texte simple'
      ],
      
      subscriptionTitle: 'Conditions d\'abonnement',
      subscriptionPoints: [
        'Abonnement mensuel: 7.99\u20AC/mois',
        'Annulez \u00E0 tout moment - sans engagement',
        'L\'abonnement reste actif jusqu\'\u00E0 la fin de la p\u00E9riode de facturation',
        'Paiements trait\u00E9s de mani\u00E8re s\u00E9curis\u00E9e via Stripe',
        'Pas de frais cach\u00E9s ou de charges suppl\u00E9mentaires'
      ],
      
      bookingDisclaimerTitle: 'Important : Concernant la R\u00E9servation de Rendez-vous',
      bookingDisclaimerText: 'Veuillez noter que l\'abonnement \u00E0 VISAGO-ALERTS ne garantit pas la r\u00E9servation d\'un rendez-vous visa. Notre service d\u00E9tecte les cr\u00E9neaux disponibles en temps r\u00E9el et vous en informe imm\u00E9diatement par SMS et email. Cependant, les cr\u00E9neaux de rendez-vous visa sont extr\u00EAmement limit\u00E9s et tr\u00E8s demand\u00E9s \u2014 des centaines de demandeurs peuvent recevoir la m\u00EAme alerte au m\u00EAme moment.',
      bookingDisclaimerAdvice: 'Pour maximiser vos chances d\'obtenir un rendez-vous :',
      bookingDisclaimerPoints: [
        'Restez vigilant et consultez vos SMS et emails d\u00E8s leur r\u00E9ception',
        'Gardez votre t\u00E9l\u00E9phone \u00E0 port\u00E9e de main avec les notifications activ\u00E9es en permanence',
        'Pr\u00E9parez vos identifiants du centre de visa pour r\u00E9server imm\u00E9diatement',
        'Agissez en quelques secondes \u2014 les plus rapides obtiennent les cr\u00E9neaux disponibles',
        'Ne comptez pas uniquement sur l\'email ; les alertes SMS arrivent plus vite et doivent \u00EAtre votre canal principal'
      ],
      bookingDisclaimerNote: 'Nous fournissons l\'information \u2014 votre rapidit\u00E9 fait la diff\u00E9rence.',
      
      refundTitle: 'Garantie Satisfait ou Rembours\u00E9 \u2014 30 Jours',
      refundText: 'Nous croyons en la qualit\u00E9 de notre service de surveillance. Si vous vous abonnez et ne recevez aucune alerte de rendez-vous dans les 30 jours suivant le d\u00E9but de votre abonnement, vous avez droit \u00E0 un remboursement int\u00E9gral \u2014 sans conditions.',
      refundHow: 'Comment obtenir votre remboursement :',
      refundPoints: [
        'Contactez-nous dans les 30 jours suivant votre abonnement \u00E0 customer_service@visago-alerts.online',
        'Indiquez votre adresse email d\'inscription et la date de votre abonnement',
        'Si aucune alerte n\'a \u00E9t\u00E9 envoy\u00E9e \u00E0 votre compte durant cette p\u00E9riode, vous recevrez un remboursement \u00E0 100%',
        'Les remboursements sont trait\u00E9s sous 5 \u00E0 7 jours ouvrables via le moyen de paiement d\'origine'
      ],
      refundNote: 'Cette garantie s\'applique uniquement si aucune alerte n\'a \u00E9t\u00E9 envoy\u00E9e pour votre destination et pays de r\u00E9sidence s\u00E9lectionn\u00E9s pendant la p\u00E9riode de 30 jours. Si des alertes ont \u00E9t\u00E9 envoy\u00E9es mais que vous n\'avez pas pu r\u00E9server un rendez-vous, la garantie ne s\'applique pas, car la disponibilit\u00E9 des rendez-vous d\u00E9pend des centres de visa externes ind\u00E9pendants de notre volont\u00E9.',
      
      contactTitle: 'Contact',
      contactText: 'Pour toute question, veuillez utiliser notre formulaire de contact ou nous écrire à:',
      contactEmail: 'customer_service@visago-alerts.online'
    }
  };

  const t = content[language] || content.en;

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="impressum-page">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-[#0A0A0A] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="font-heading text-4xl font-bold text-[#0A0A0A] mb-8" data-testid="impressum-title">
            {t.title}
          </h1>

          {/* Company Info */}
          <Card className="rounded-sm border-0 shadow-sm mb-6">
            <CardContent className="p-6">
              <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.company}</h2>
              <p className="text-gray-700">
                <strong>{t.companyName}</strong><br />
                {t.address}
              </p>
            </CardContent>
          </Card>

          {/* Service Description */}
          <Card className="rounded-sm border-0 shadow-sm mb-6">
            <CardContent className="p-6">
              <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.serviceTitle}</h2>
              <p className="text-gray-700">{t.serviceText}</p>
            </CardContent>
          </Card>

          {/* Disclaimer */}
          <Card className="rounded-sm border-0 shadow-sm mb-6 border-l-4 border-l-yellow-500">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.disclaimerTitle}</h2>
                  <p className="text-gray-700 mb-4 bg-yellow-50 p-3 rounded-sm">{t.disclaimerText}</p>
                  <ul className="space-y-2">
                    {t.disclaimerPoints.map((point, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-yellow-500 mt-1">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Security */}
          <Card className="rounded-sm border-0 shadow-sm mb-6 bg-green-50">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Shield className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.securityTitle}</h2>
                  <p className="text-gray-700 mb-4">{t.securityText}</p>
                  <ul className="space-y-2">
                    {t.securityPoints.map((point, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-green-600 mt-1">✓</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Terms */}
          <Card className="rounded-sm border-0 shadow-sm mb-6">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <CreditCard className="w-6 h-6 text-[#0A0A0A] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.subscriptionTitle}</h2>
                  <ul className="space-y-2">
                    {t.subscriptionPoints.map((point, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-[#0A0A0A] mt-1">•</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Booking Disclaimer */}
          <Card className="rounded-sm border-0 shadow-sm mb-6 border-l-4 border-l-[#FF3B30]" data-testid="booking-disclaimer">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Clock className="w-6 h-6 text-[#FF3B30] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.bookingDisclaimerTitle}</h2>
                  <p className="text-gray-700 mb-4 bg-red-50 p-4 rounded-sm leading-relaxed">{t.bookingDisclaimerText}</p>
                  <p className="text-gray-800 font-medium mb-3">{t.bookingDisclaimerAdvice}</p>
                  <ul className="space-y-2 mb-4">
                    {t.bookingDisclaimerPoints.map((point, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-[#FF3B30] mt-1 font-bold">{idx + 1}.</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[#0A0A0A] font-semibold italic border-t pt-3">{t.bookingDisclaimerNote}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 30-Day Refund Guarantee */}
          <Card className="rounded-sm border-0 shadow-sm mb-6 border-l-4 border-l-green-500 bg-green-50/50" data-testid="refund-guarantee">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <RefreshCw className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.refundTitle}</h2>
                  <p className="text-gray-700 mb-4 leading-relaxed">{t.refundText}</p>
                  <p className="text-gray-800 font-medium mb-3">{t.refundHow}</p>
                  <ul className="space-y-2 mb-4">
                    {t.refundPoints.map((point, idx) => (
                      <li key={idx} className="text-gray-700 flex items-start gap-2">
                        <span className="text-green-600 mt-1">&#10003;</span>
                        {point}
                      </li>
                    ))}
                  </ul>
                  <p className="text-gray-600 text-sm bg-white/70 p-3 rounded-sm border border-green-200 leading-relaxed">{t.refundNote}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact */}
          <Card className="rounded-sm border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Mail className="w-6 h-6 text-[#0A0A0A] flex-shrink-0 mt-1" />
                <div>
                  <h2 className="font-heading font-semibold text-xl text-[#0A0A0A] mb-4">{t.contactTitle}</h2>
                  <p className="text-gray-700 mb-3">{t.contactText}</p>
                  <a 
                    href="mailto:customer_service@visago-alerts.online" 
                    className="inline-flex items-center gap-2 text-[#FF3B30] hover:underline font-medium"
                  >
                    <Mail className="w-4 h-4" />
                    {t.contactEmail}
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Last Updated */}
          <p className="text-center text-gray-500 text-sm mt-8">
            Last updated: March 2026
          </p>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ImpressumPage;
